import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthClient } from '@/auth/useAuth';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { resolveReturnTo } from '@/auth/returnTo';
import { CenterCard } from '@/components/CenterCard';

interface CallbackState {
    returnTo?: string;
}

/**
 * `/callback` — OIDC redirect handler (EAA-1756). Runs the authorization-code
 * exchange via the auth-client, then returns the browser to the `returnTo`
 * stashed in `login()`'s `state`. EAA-1765: `returnTo` may be an allowlisted
 * EXTERNAL deep link (the portal-as-central-login flow) — in that case we leave
 * the portal via `window.location.replace`; otherwise we navigate internally.
 * Always re-validated against the allowlist (defence in depth — never trust the
 * value in `state` blindly). Shows a friendly error if the exchange fails.
 */
export function CallbackPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const authClient = useAuthClient();
    const config = useRuntimeConfig();
    const [error, setError] = useState<string | null>(null);
    const ranOnce = useRef(false);

    useEffect(() => {
        // StrictMode double-invokes effects in dev; signinRedirectCallback()
        // consumes a one-time-use authorization code, so guard against a
        // second call which would otherwise fail as "no matching state".
        if (ranOnce.current) return;
        ranOnce.current = true;

        authClient
            .handleCallback()
            .then((user) => {
                const state = user.state as CallbackState | undefined;
                const resolved = resolveReturnTo(state?.returnTo, config.returnToAllowlist ?? []);
                if (resolved.kind === 'external') {
                    window.location.replace(resolved.url);
                } else {
                    navigate(resolved.path, { replace: true });
                }
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : t('profile.callback.failedMessage'));
            });
    }, [authClient, config.returnToAllowlist, navigate, t]);

    if (error) {
        return (
            <CenterCard title={t('profile.callback.failedTitle')} showSpinner={false}>
                <p className="portal-callback-error">{error}</p>
                <a className="portal-btn portal-btn-primary" href="/">
                    {t('profile.callback.retry')}
                </a>
            </CenterCard>
        );
    }

    return (
        <CenterCard title={t('profile.callback.loadingTitle')}>
            <p>{t('profile.callback.loadingDescription')}</p>
        </CenterCard>
    );
}
